import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FlatVsTiered = () => {
  useEffect(() => {
    analytics.track('Comparison Page View', { comparison: 'Flat vs Tiered Pricing' });
  }, []);

  return (
    <SEOPageLayout
      title="Flat Rate vs Tiered Pricing: Which Makes More Money? | QuoteTier"
      description="Is flat rate pricing costing you money? See the data on why Good/Better/Best tiered pricing consistently outperforms single-option quotes."
      canonical="/compare/flat-rate-vs-tiered-pricing"
      ogTitle="Stop Giving Clients Yes/No Choices"
      ogDescription="Flat rate pricing forces a binary decision: Buy or Don't Buy. Tiered pricing changes the question to: Which one should I buy?"
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Flat Rate vs. <span className="text-primary font-bold">Tiered Pricing</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Why giving customers 3 choices is scientifically proven to increase your revenue.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">Feature</th>
                  <th className="text-center py-6 px-6 bg-black text-white">
                      <span className="font-instrument text-lg font-bold">Tiered Pricing</span>
                  </th>
                  <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">Flat Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Customer Choice</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">High (3 options)</td>
                  <td className="py-4 px-6 text-center text-red-500">None (Take it or leave it)</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Average Ticket Size</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">Higher (+30% avg)</td>
                  <td className="py-4 px-6 text-center">Baseline</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-6 font-jetbrains font-medium">Closing Rate</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">Higher</td>
                  <td className="py-4 px-6 text-center">Lower</td>
                </tr>
                <tr className="border-b border-gray-100 bg-gray-50/30">
                  <td className="py-4 px-6 font-jetbrains font-medium">Upsell Effort</td>
                  <td className="py-4 px-6 text-center text-green-600 font-bold">Automated</td>
                  <td className="py-4 px-6 text-center text-red-500">Manual / High Effort</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container max-w-4xl mx-auto px-4">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">The Psychology of "No"</h2>
            <p className="font-jetbrains text-lg text-gray-700 mb-6">
                When you present a single price (Flat Rate), you force the customer into a binary decision: "Is this worth $5,000? Yes or No?"
            </p>
            <p className="font-jetbrains text-lg text-gray-700 mb-6">
                If the answer is "No", you lose the job.
            </p>
            <p className="font-jetbrains text-lg text-gray-700 mb-6">
                When you present three options (Good/Better/Best), the psychology shifts completely. The customer is now comparing the options against each other, not against zero.
            </p>
            <p className="font-jetbrains text-lg text-gray-700 mb-6">
                "I can't afford the $8,000 Platinum package, but the $5,000 Gold package seems like much better value than the $3,500 Silver package."
            </p>
            <p className="font-jetbrains text-lg text-gray-700 font-bold">
                You just sold a $5,000 job that might have been a "No" if presented alone.
            </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Switch to Tiered Pricing Today</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Start Free
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default FlatVsTiered;
